import { observer } from 'mobx-react-lite';
import { userInteractionAction, ViewType } from 'owa-calendar-actions';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import loc, { formatToArray } from 'owa-localize';
import { LocationCard } from 'owa-location-card';
import { locationDeclinedFormat } from 'owa-locstrings/lib/strings/locationdeclinedtext.locstring.json';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import * as React from 'react';
import { containsWebUrlLocation, WebUrlLocation } from 'owa-location-persona';
import { noRoomAvailable, assistantProcessing } from './AutoRoomBookingStatus.locstring.json';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { RoomRequestStatus } from 'owa-graph-schema';

import styles from './InlineLocations.scss';

export interface InlineLocationsProps {
    locations: EnhancedLocation[];
    actionSource: ViewType;
    isOrganizer: boolean;
    isMeeting: boolean;
    className?: string;
    colorScheme?: ColorScheme;
    disableCards?: boolean;
    resources?: AttendeeType[];
    isRoomRequested?: boolean;
    autoRoomBookingStatus?: RoomRequestStatus;
}

/**
 * Returns the UI elements for the locations line displayed in the reading pane and the peek
 */
export default observer(function InlineLocations(props: InlineLocationsProps) {
    const {
        locations,
        actionSource,
        isOrganizer,
        isMeeting,
        className,
        colorScheme,
        disableCards,
        resources,
        isRoomRequested,
        autoRoomBookingStatus,
    } = props;

    if (!locations || locations.length == 0) {
        return getAutoRoomBookingStatus(isRoomRequested, autoRoomBookingStatus);
    }

    const locationElements = locations
        .map((location, index) => {
            if (location) {
                // convert string locations that are web addresses to a link, and wrap other locations with LocationCard
                if (containsWebUrlLocation(location)) {
                    return <WebUrlLocation location={location.DisplayName} />;
                } else {
                    const resource = getResourceForLocation(location, resources);
                    const locationDeclined = resource && resource.ResponseType === 'Decline';

                    const locationElement = (
                        <span className={className}>
                            {location.DisplayName}
                            {index < locations.length - 1 && !locationDeclined && '; '}
                        </span>
                    );

                    const locationCard = (
                        <LocationCard
                            name={location.DisplayName}
                            postalAddress={location.PostalAddress}
                            key={location.Id}
                            locationId={location.Id}
                            colorScheme={colorScheme}
                            onLocationCardUnmounted={logUserInteractionDatapoint(
                                actionSource,
                                isOrganizer,
                                isMeeting
                            )}
                            locationAriaLabel={location.DisplayName}>
                            {locationElement}
                        </LocationCard>
                    );

                    if (disableCards) {
                        return locationElement;
                    } else if (!locationDeclined) {
                        return locationCard;
                    } else {
                        return (
                            <span className={styles.locationDeclinedString}>
                                {formatToArray(loc(locationDeclinedFormat), locationCard)}
                                {index < locations.length - 1 && locationDeclined && '; '}
                            </span>
                        );
                    }
                }
            }
            return null;
        })
        .filter(x => !!x);

    return <>{locationElements}</>;
});

function logUserInteractionDatapoint(
    actionSource: ViewType,
    isOrganizer: boolean,
    isMeeting: boolean
) {
    return function () {
        // Log UserInteractions datapoint when the location card is dismissed(unmounted)
        userInteractionAction('LinkClicked', actionSource, isOrganizer, isMeeting, [
            'LocationCard',
        ]);
    };
}

// Get the corresponding resource for a specific location
function getResourceForLocation(
    location: EnhancedLocation,
    resources: AttendeeType[]
): AttendeeType {
    if (
        resources &&
        location.PostalAddress &&
        location.PostalAddress.LocationSource === 'Resource'
    ) {
        const resource = resources.filter(
            resource =>
                resource?.Mailbox?.EmailAddress &&
                location.PostalAddress.LocationUri &&
                resource.Mailbox.EmailAddress.toLowerCase() ===
                    location.PostalAddress.LocationUri.toLowerCase()
        );

        return resource.length > 0 ? resource[0] : null;
    }

    return null;
}

function getAutoRoomBookingStatus(
    isRoomRequested: boolean,
    status: RoomRequestStatus
): JSX.Element {
    if (!isRoomRequested) {
        return null;
    }

    if (status == null || status == 'None') {
        return renderAutoRoomBookingStatus(false /*hasIcon*/, loc(assistantProcessing));
    } else if (status == 'Unavailable') {
        return renderAutoRoomBookingStatus(true /*hasIcon*/, loc(noRoomAvailable));
    }

    return null;
}

function renderAutoRoomBookingStatus(hasIcon: boolean, content: string): JSX.Element {
    return (
        <div className={styles.autoRoomBookingStatusContainer}>
            {hasIcon && (
                <div className={styles.errorIconContainer}>
                    <Icon iconName={ControlIcons.Error} title={content} />
                </div>
            )}
            <span>{content}</span>
        </div>
    );
}
